import React, { useCallback, useMemo, useState } from 'react';

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

export default function Rooms() {
  const [activeView, setActiveView] = useState('guesthouse-wise');

  // Get user from localStorage (same as Header.jsx does)
  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  if (!localUser) {
    return null;
  }

  const moduleId = parseInt(localUser.moduleId, 10);

  // Determine visible buttons based on moduleId
  const showRoomList = moduleId === 10;
  const showEditRoom = moduleId === 10;

  const handleButtonClick = (view) => {
    setActiveView(view);
  };

  return (
    <div className="page-content">
      <div style={{ marginBottom: '20px' }}>
        <button
          className="btn btn-primary"
          onClick={() => handleButtonClick('guesthouse-wise')}
          style={{ marginRight: '10px' }}
        >
          GuestHouse Wise
        </button>
        {showRoomList && (
          <button
            className="btn btn-primary"
            onClick={() => handleButtonClick('room-list')}
            style={{ marginRight: '10px' }}
          >
            Room List
          </button>
        )}
        {showEditRoom && (
          <button className="btn btn-primary" onClick={() => handleButtonClick('edit-room')}>
            Edit Room
          </button>
        )}
      </div>

      {activeView === 'guesthouse-wise' && (
        <div>
          <h3 className="ctitle">GuestHouse Wise</h3>
          <GuesthouseWiseView />
        </div>
      )}

      {activeView === 'room-list' && (
        <div>
          <h3 className="ctitle">Room List</h3>
          <RoomListView />
        </div>
      )}

      {activeView === 'edit-room' && (
        <div>
          <h3 className="ctitle">Edit Room</h3>
          <EditRoomView />
        </div>
      )}
    </div>
  );
}

// GuestHouse Wise Component
function GuesthouseWiseView() {
  const [guestHouses, setGuestHouses] = useState([]);
  const [selectedGuestHouse, setSelectedGuestHouse] = useState('');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const localUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const moduleId = localUser?.moduleId;

  const fetchGuestHouses = useCallback(async () => {
    if (!moduleId) return;

    try {
      const response = await fetch(`${API_ROOT}/api/guesthouse/rooms/options?moduleId=${moduleId}`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Failed to fetch guest houses');
      const data = await response.json();
      setGuestHouses(data.data || []);
    } catch (error) {
      console.error('Error fetching guest houses:', error);
    }
  }, [moduleId]);

  React.useEffect(() => {
    fetchGuestHouses();
  }, [fetchGuestHouses]);

  const handleViewRooms = async () => {
    if (!selectedGuestHouse) {
      alert('Please select a guest house');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_ROOT}/api/guesthouse/rooms/list?moduleId=${moduleId}&guestHouseID=${selectedGuestHouse}`,
        { method: 'GET' }
      );
      if (!response.ok) throw new Error('Failed to fetch rooms');
      const data = await response.json();
      setRooms(data.data || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      alert('Error loading rooms');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-lg-10">
      <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
        <select
          className="form-control"
          value={selectedGuestHouse}
          onChange={(e) => setSelectedGuestHouse(e.target.value)}
          style={{ width: '300px' }}
        >
          <option value="">Select Guest House</option>
          {guestHouses.map((gh) => (
            <option key={gh.GuestHouseID} value={gh.GuestHouseID}>
              {gh.GuestHouse}
            </option>
          ))}
        </select>
        <button
          className="btn btn-success"
          onClick={handleViewRooms}
          style={{ marginLeft: '10px', whiteSpace: 'nowrap' }}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'View'}
        </button>
      </div>

      {rooms.length > 0 && (
        <table style={{ borderCollapse: 'collapse', width: '100%', border: '1px solid #dee2e6' }}>
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr style={{ backgroundColor: '#f2f2f2' }}>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>SNo</th>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Guest House</th>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Room</th>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Active</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((room, index) => (
              <tr key={index}>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{index + 1}</td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{room.GuestHouse}</td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{room.Room}</td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{room.Active === 1 ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Room List Component
function RoomListView() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchAllRooms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ROOT}/api/guesthouse/rooms/all-rooms?moduleId=${localUser.moduleId}`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Failed to fetch rooms');
      const data = await response.json();
      setRooms(data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [localUser?.moduleId]);

  React.useEffect(() => {
    fetchAllRooms();
  }, [fetchAllRooms]);

  return (
    <div className="col-lg-10">
      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <p>Loading rooms...</p>
      ) : rooms.length > 0 ? (
        <table style={{ borderCollapse: 'collapse', width: '100%', border: '1px solid #dee2e6' }}>
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th colSpan="4" style={{ padding: '8px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                -- Date : {new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}
              </th>
            </tr>
            <tr style={{ backgroundColor: '#f2f2f2' }}>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>SNo</th>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Guest House</th>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Room</th>
              <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Active</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((room, index) => (
              <tr key={index}>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{index + 1}</td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{room.GuestHouse}</td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{room.Room}</td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{room.Active === 1 ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No rooms found</p>
      )}
    </div>
  );
}

// Edit Room Component
function EditRoomView() {
  const [guestHouses, setGuestHouses] = useState([]);
  const [selectedGuestHouse, setSelectedGuestHouse] = useState('');
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [editRoomName, setEditRoomName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchGuestHouses = useCallback(async () => {
    try {
      const response = await fetch(`${API_ROOT}/api/guesthouse/rooms/options?moduleId=${localUser.moduleId}`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Failed to fetch guest houses');
      const data = await response.json();
      setGuestHouses(data.data || []);
    } catch (error) {
      console.error('Error fetching guest houses:', error);
    }
  }, [localUser?.moduleId]);

  React.useEffect(() => {
    if (localUser && parseInt(localUser.moduleId, 10) === 10) {
      fetchGuestHouses();
    }
  }, [fetchGuestHouses, localUser]);

  // Only allow moduleId 10 access - must be after all hooks
  if (!localUser || parseInt(localUser.moduleId, 10) !== 10) {
    return <div className="alert alert-danger">You do not have permission to edit rooms</div>;
  }

  const handleGuestHouseChange = async (e) => {
    const ghId = e.target.value;
    setSelectedGuestHouse(ghId);
    setSelectedRoom('');
    setEditRoomName('');
    setIsActive(false);

    if (ghId) {
      try {
        const response = await fetch(
          `${API_ROOT}/api/guesthouse/rooms/by-guesthouse?moduleId=${localUser.moduleId}&guestHouseID=${ghId}`,
          { method: 'GET' }
        );
        if (!response.ok) throw new Error('Failed to fetch rooms');
        const data = await response.json();
        setRooms(data.data || []);
      } catch (error) {
        console.error('Error fetching rooms:', error);
        alert('Error loading rooms for this guest house');
      }
    }
  };

  const handleRoomChange = (e) => {
    const roomId = e.target.value;
    setSelectedRoom(roomId);

    // Find room details and populate form
    const room = rooms.find((r) => r.RoomID === parseInt(roomId));
    if (room) {
      setEditRoomName(room.Room || '');
      setIsActive(room.Active === 1);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!selectedGuestHouse || !selectedRoom || !editRoomName) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_ROOT}/api/guesthouse/rooms/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          moduleId: localUser.moduleId,
          roomId: selectedRoom,
          guestHouseId: selectedGuestHouse,
          roomName: editRoomName,
          active: isActive ? 1 : 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update room');
      }

      alert('Room Updated');
      // Reset form fields after successful update
      setSelectedGuestHouse('');
      setSelectedRoom('');
      setEditRoomName('');
      setIsActive(false);
      setRooms([]);
    } catch (error) {
      console.error('Error updating room:', error);
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-sm-8">
      <form onSubmit={handleUpdate}>
        <div className="form-group" style={{ marginBottom: '15px' }}>
          <label>Guest House</label>
          <select
            className="form-control"
            value={selectedGuestHouse}
            onChange={handleGuestHouseChange}
            required
          >
            <option value="">Select Guest House</option>
            {guestHouses.map((gh) => (
              <option key={gh.GuestHouseID} value={gh.GuestHouseID}>
                {gh.GuestHouse}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: '15px' }}>
          <label>Room No.</label>
          <select
            className="form-control"
            value={selectedRoom}
            onChange={handleRoomChange}
            required
            disabled={!selectedGuestHouse}
          >
            <option value="">Select Room</option>
            {rooms.map((room) => (
              <option key={room.RoomID} value={room.RoomID}>
                {room.Room}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: '15px' }}>
          <label>Edit Room No.</label>
          <input
            type="text"
            className="form-control"
            value={editRoomName}
            onChange={(e) => setEditRoomName(e.target.value)}
            placeholder="Room...*"
            required
          />
        </div>

        <div className="form-group" style={{ marginBottom: '15px' }}>
          <label>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active
          </label>
        </div>

        <div className="form-group">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>
    </div>
  );
}
